/**
 * Brücke: Domain-Event-Bus + Legacy window-Events (Übergangsphase).
 *
 * High-chatter publishers coalesce within one microtask (last detail wins)
 * so Jul-9 dual domain+window fan-out does not multiply UI/KV work.
 */

import { publishDomainEvent, DOMAIN_EVENTS } from './domainEventBus';

function dispatchWindowEvent(name, detail = {}) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

/** @type {Map<string, { run: () => void }>} */
const coalesceQueue = new Map();
let coalesceFlushScheduled = false;

function scheduleCoalescedPublish(key, run) {
  coalesceQueue.set(key, { run });
  if (coalesceFlushScheduled) return;
  coalesceFlushScheduled = true;
  queueMicrotask(() => {
    coalesceFlushScheduled = false;
    const batch = [...coalesceQueue.values()];
    coalesceQueue.clear();
    for (const item of batch) {
      try {
        item.run();
      } catch (err) {
        console.warn('[legacyEventBridge] coalesced publish failed:', err?.message || err);
      }
    }
  });
}

export function publishLotUpdated(detail = {}) {
  scheduleCoalescedPublish('lotUpdated', () => {
    publishDomainEvent(DOMAIN_EVENTS.INVENTORY_UPDATED, detail);
    dispatchWindowEvent('lotUpdated', detail);
  });
}

export function publishInstantCacheUpdated(detail = {}) {
  scheduleCoalescedPublish('instantCacheUpdated', () => {
    publishDomainEvent(DOMAIN_EVENTS.CACHE_UPDATED, detail);
    dispatchWindowEvent('instantCacheUpdated', detail);
  });
}

export function publishSalesUpdated(detail = {}) {
  publishDomainEvent(DOMAIN_EVENTS.SALES_UPDATED, detail);
  dispatchWindowEvent('salesUpdated', detail);
}

export function publishCustomersUpdated(detail = {}) {
  publishDomainEvent(DOMAIN_EVENTS.CUSTOMERS_UPDATED, detail);
  dispatchWindowEvent('customerUpdated', detail);
}

export function publishProductsUpdated(detail = {}) {
  publishDomainEvent(DOMAIN_EVENTS.PRODUCTS_UPDATED, detail);
  dispatchWindowEvent('productUpdated', detail);
  dispatchWindowEvent('productsCacheUpdated', detail);
}

export function publishPurchasesUpdated(detail = {}) {
  publishDomainEvent(DOMAIN_EVENTS.PURCHASES_UPDATED, detail);
  dispatchWindowEvent('purchasesCacheUpdated', detail);
}

export function publishSuppliersUpdated(detail = {}) {
  publishDomainEvent(DOMAIN_EVENTS.SUPPLIERS_UPDATED, detail);
  dispatchWindowEvent('suppliersCacheUpdated', detail);
}

export function publishOrdersUpdated(detail = {}) {
  publishDomainEvent(DOMAIN_EVENTS.ORDERS_UPDATED, detail);
}

export function publishDataChanged(detail = {}) {
  publishDomainEvent(DOMAIN_EVENTS.DATA_CHANGED, detail);
  dispatchWindowEvent('dataChanged', detail);
}

export function publishWorkerSessionUpdated(detail = {}) {
  publishDomainEvent(DOMAIN_EVENTS.WORKER_SESSION_UPDATED, detail);
  dispatchWindowEvent('workerSessionUpdated', detail);
}

export function publishEcartUpdated(detail = {}) {
  publishDomainEvent(DOMAIN_EVENTS.ECART_UPDATED, detail);
  dispatchWindowEvent('ecartUpdated', detail);
}

export function publishDeviationNotification(detail = {}) {
  publishDomainEvent(DOMAIN_EVENTS.DEVIATION_NOTIFICATION, detail);
  dispatchWindowEvent('createDeviationNotification', detail);
}

export function publishNotificationResolve(detail = {}) {
  publishDomainEvent(DOMAIN_EVENTS.NOTIFICATION_RESOLVE, detail);
}

export function publishStockLocationsUpdated(detail = {}) {
  publishDomainEvent(DOMAIN_EVENTS.STOCK_LOCATIONS_UPDATED, detail);
  dispatchWindowEvent('stockLocationsUpdated', detail);
}

export function publishBusinessLogoUpdated(detail = {}) {
  publishDomainEvent(DOMAIN_EVENTS.BUSINESS_LOGO_UPDATED, detail);
  dispatchWindowEvent('businessLogoUpdated', detail);
}

export function publishPurchaseAdded(detail = {}) {
  publishPurchasesUpdated(detail);
  dispatchWindowEvent('purchaseAdded', detail);
}

export function publishSupplierPaymentRecorded(detail = {}) {
  publishDomainEvent(DOMAIN_EVENTS.SUPPLIER_PAYMENT_RECORDED, detail);
  dispatchWindowEvent('supplierPaymentRecorded', detail);
}

export function publishSupplierInvoiceChanged(detail = {}) {
  publishDomainEvent(DOMAIN_EVENTS.SUPPLIER_INVOICE_CHANGED, detail);
  dispatchWindowEvent('supplierInvoiceChanged', detail);
}

export function publishExpenseRecorded(detail = {}) {
  publishDomainEvent(DOMAIN_EVENTS.EXPENSE_RECORDED, detail);
  dispatchWindowEvent('expenseRecorded', detail);
}

export function publishSaleAdded(detail = {}) {
  publishSalesUpdated(detail);
  dispatchWindowEvent('saleAdded', detail);
}

/**
 * @param {string} name — Legacy window event name
 * @param {object} [detail]
 */
export function publishLegacyWindowEvent(name, detail = {}) {
  switch (name) {
    case 'instantCacheUpdated':
      publishInstantCacheUpdated(detail);
      return;
    case 'salesUpdated':
      publishSalesUpdated(detail);
      return;
    case 'lotUpdated':
      publishLotUpdated(detail);
      return;
    case 'stockUpdated':
      scheduleCoalescedPublish('stockUpdated', () => {
        publishDomainEvent(DOMAIN_EVENTS.INVENTORY_UPDATED, detail);
        dispatchWindowEvent('stockUpdated', detail);
      });
      return;
    case 'productUpdated':
      publishProductsUpdated(detail);
      return;
    case 'productsCacheUpdated':
      publishProductsUpdated(detail);
      return;
    case 'purchasesCacheUpdated':
      publishPurchasesUpdated(detail);
      return;
    case 'suppliersCacheUpdated':
      publishSuppliersUpdated(detail);
      return;
    case 'customerUpdated':
      publishCustomersUpdated(detail);
      return;
    case 'dataChanged':
      publishDataChanged(detail);
      return;
    case 'inventoryUpdated':
      scheduleCoalescedPublish('inventoryUpdated', () => {
        publishDomainEvent(DOMAIN_EVENTS.INVENTORY_UPDATED, detail);
        dispatchWindowEvent('inventoryUpdated', detail);
      });
      return;
    case 'dataUpdated':
      if (detail.type === 'stock' || detail.type === 'lots') {
        publishDomainEvent(DOMAIN_EVENTS.INVENTORY_UPDATED, detail);
      } else {
        publishDomainEvent(DOMAIN_EVENTS.DATA_CHANGED, detail);
      }
      dispatchWindowEvent('dataUpdated', detail);
      return;
    case 'workerSessionUpdated':
      publishWorkerSessionUpdated(detail);
      return;
    case 'ecartUpdated':
      publishEcartUpdated(detail);
      return;
    case 'createDeviationNotification':
      publishDeviationNotification(detail);
      return;
    case 'stockLocationsUpdated':
      publishStockLocationsUpdated(detail);
      return;
    case 'businessLogoUpdated':
      publishBusinessLogoUpdated(detail);
      return;
    case 'purchaseAdded':
      publishPurchaseAdded(detail);
      return;
    case 'forceStockRefresh':
      scheduleCoalescedPublish('forceStockRefresh', () => {
        publishDomainEvent(DOMAIN_EVENTS.INVENTORY_UPDATED, { ...detail, forceRefresh: true });
        dispatchWindowEvent('forceStockRefresh', detail);
      });
      return;
    case 'reloadBusinessData':
      publishDomainEvent(DOMAIN_EVENTS.BUSINESS_PAYLOAD_UPDATED, detail);
      dispatchWindowEvent('reloadBusinessData', detail);
      return;
    case 'saleAdded':
      publishSaleAdded(detail);
      return;
    default:
      break;
  }
  dispatchWindowEvent(name, detail);
}

export function publishReloadBusinessData(detail = {}) {
  publishLegacyWindowEvent('reloadBusinessData', detail);
}

export function publishForceStockRefresh(detail = {}) {
  publishLegacyWindowEvent('forceStockRefresh', detail);
}
